import { type CalendarEventData, EventCategoryIconMap } from "@qendo/calendar";
import {
    CycleKey,
    type FlareNotificationTarget,
    type FlareTarget,
    NotificationFrequency,
    NotificationOptions,
    type NotificationTarget,
    NotificationTitles,
    NotificationTriggerGroup,
    type ScheduleNotificationRequest,
    type ScheduleNotificationTarget,
    type TitledNotification,
} from "@qendo/notifications";
import type { JournalData } from "@qendo/profile/journal";
import { type BasicScheduleTimeData, EmptyWeek, type WeekdayData } from "@qendo/schedule";
import { LegacySymptomId, TrackableType } from "@qendo/trackables";
import type { WeekDates, Weekday } from "@qendo/util";
import { addDays, type DateArg, isPast, startOfDay } from "date-fns";
import { type NotificationRequest, SchedulableTriggerInputTypes } from "expo-notifications";
import type { Href } from "expo-router";
import type { QendoIconSet } from "~/components/QendoIcon";
import type { NotificationSettings } from "~/entities/notification-settings";
import { filled } from "~/util";
import { randomElement } from "~/util/array";
import { formatDateString, formatTimeString, mergeDateTime } from "~/util/dates";
import { randomString } from "~/util/string";

export function makeKey(
    target: string,
    group: NotificationTriggerGroup,
    date?: DateArg<Date> | null,
    time?: DateArg<Date> | null,
): string {
    return [
        target,
        group,
        date && group !== NotificationTriggerGroup.interval ? formatDateString(date) : null,
        time ? formatTimeString(time) : null,
    ]
        .filter(filled)
        .join("|");
}

export function makeIdentifier(
    target: string,
    area: NotificationTriggerGroup,
    date?: DateArg<Date> | null,
    time?: DateArg<Date> | null,
): string {
    return [makeKey(target, area, date, time), randomString()].filter(filled).join("|");
}

export function filterSchedule(schedule: NotificationRequest[], key: string): NotificationRequest[] {
    return schedule.filter(({ identifier }) => identifier.includes(key));
}

export function findScheduled(
    schedule: NotificationRequest[],
    target: string,
    group: NotificationTriggerGroup,
    date?: DateArg<Date> | null,
    time?: DateArg<Date> | null,
) {
    const key = makeKey(target, group, date, time);

    return schedule.find(({ identifier }) => identifier.includes(key)) ?? null;
}

export function hasScheduled(
    schedule: NotificationRequest[],
    target: string,
    group: NotificationTriggerGroup,
    date?: DateArg<Date> | null,
    time?: DateArg<Date> | null,
): boolean {
    return Boolean(findScheduled(schedule, target, group, date, time));
}

export function hasFlare(journals: JournalData[]): boolean {
    return journals.some((j) => j.flare);
}
export function notifyOnFlare(settings: NotificationSettings): FlareNotificationTarget[] {
    return Object.entries(settings)
        .filter(([_k, v]) => typeof v === "object" && v.frequency === NotificationFrequency.flare && v.times.length)
        .map(([key, { times }]) => ({ key, times })) as FlareNotificationTarget[];
}

export function notifyOnSchedule(settings: NotificationSettings): ScheduleNotificationTarget[] {
    return Object.entries(settings)
        .filter(([_k, v]) => typeof v === "object" && v.frequency !== NotificationFrequency.flare && v.times.length)
        .map<ScheduleNotificationTarget>(([key, { frequency, times, days, interval }]) => ({
            key: key as NotificationTarget,
            frequency: frequency as NotificationTriggerGroup,
            times: times as BasicScheduleTimeData[],
            days: days ?? EmptyWeek,
            interval: interval ?? 0,
        }));
}

function pickIcon(group: NotificationTriggerGroup, target?: NotificationTarget | CycleKey): QendoIconSet {
    return (
        ((group === NotificationTriggerGroup.pain || target === "log_pain") && "pain") ||
        (target === "drink_water" && "water") ||
        (target === "pelvic_floor" && "swelling") ||
        (target === "log_symptom" && "symptoms") ||
        (target === "move_your_body" && "movement") ||
        (target === "pelvic_exercises" && "exercise") ||
        (["breath", "rest"].includes(target as NotificationTarget) && "relaxation") ||
        (target === CycleKey.ovulation && "ovulation") ||
        (Object.values(CycleKey).includes(target as CycleKey) && "bleeding") ||
        (group === NotificationTriggerGroup.flare ? "zap" : "heart")
    );
}

export function makePainNotification(): ScheduleNotificationRequest {
    const body = randomElement(NotificationOptions.pain);
    const identifier = makeIdentifier("", NotificationTriggerGroup.pain);
    const icon = pickIcon(NotificationTriggerGroup.pain);

    return {
        identifier,
        content: {
            title: "Pain Level Check-In",
            body,
            data: { icon },
        },
        trigger: {
            type: SchedulableTriggerInputTypes.TIME_INTERVAL,
            repeats: false,
            seconds: 30,
        },
    };
}

export function makeFlareNotification(target: FlareTarget, time: DateArg<Date>): ScheduleNotificationRequest {
    const tomorrow = startOfDay(addDays(new Date(), 1));
    const notify_at = mergeDateTime(tomorrow, time);

    const title = Object.hasOwn(NotificationTitles, target)
        ? NotificationTitles[target as TitledNotification]
        : "After your flare";
    const body = Object.hasOwn(NotificationOptions, target)
        ? randomElement(NotificationOptions[target as NotificationTarget])
        : undefined;
    const identifier = makeIdentifier(target, NotificationTriggerGroup.flare, tomorrow, time);
    const icon = pickIcon(NotificationTriggerGroup.flare, target);

    return {
        identifier,
        content: {
            title,
            body,
            data: { icon },
        },
        trigger: {
            type: SchedulableTriggerInputTypes.DATE,
            date: notify_at,
        },
    };
}

export function makeCycleNotification(target: CycleKey, date: DateArg<Date>): ScheduleNotificationRequest {
    return {
        identifier: makeIdentifier(target, NotificationTriggerGroup.cycle, date),
        content: {
            title: NotificationTitles[target],
            body: randomElement(NotificationOptions[target]),
            data: {
                icon: target === "ovulation_prediction" ? "ovulation" : "bleeding",
            },
        },
        trigger: {
            type: SchedulableTriggerInputTypes.DATE,
            date: mergeDateTime(date, "08:00:00"),
        },
    };
}

export function makeCalendarNotification(appointment: CalendarEventData, date: Date): ScheduleNotificationRequest {
    const title = NotificationTitles.appointment;
    const body = randomElement(NotificationOptions.appointment);
    const icon = EventCategoryIconMap[appointment.category];
    const href: Href = { pathname: "/calendar/[id]/show", params: { id: appointment.id } };

    return {
        identifier: makeIdentifier(appointment.id, NotificationTriggerGroup.calendar, date),
        content: {
            title,
            body,
            data: { icon, href },
        },
        trigger: {
            type: SchedulableTriggerInputTypes.DATE,
            date,
        },
    };
}

export function makeScheduledNotification(
    target: NotificationTarget,
    group: NotificationTriggerGroup,
    time: DateArg<Date>,
    day?: DateArg<Date>,
    details?: string,
    link?: Href,
): ScheduleNotificationRequest {
    let date = mergeDateTime(day ?? new Date(), time);
    if (isPast(date) && !day) {
        date = addDays(date, 1);
    }

    const title = Object.hasOwn(NotificationTitles, target)
        ? NotificationTitles[target as TitledNotification]
        : undefined;
    const body = [randomElement(NotificationOptions[target]), details].filter(filled).join("\n");
    const icon = pickIcon(group, target);
    const href: Href | undefined =
        link ||
        (target === "log_pain" && { pathname: "/journal/[id]/create", params: { id: LegacySymptomId.pain } }) ||
        (target === "log_symptom" && { pathname: "/journal/add", params: { search: TrackableType.symptom } }) ||
        undefined;

    return {
        identifier: makeIdentifier(target, group, date, time),
        content: {
            title,
            body,
            data: { icon, href },
        },
        trigger: {
            type: SchedulableTriggerInputTypes.DATE,
            date,
        },
    };
}

export function extractWeekDates(week: WeekDates, schedule: WeekdayData): Date[] {
    return Object.entries(week)
        .filter(([day]) => schedule[day as Weekday])
        .map(([_day, date]) => date);
}

export function shouldBeScheduled(
    identifier: string,
    target: NotificationTarget,
    group: NotificationTriggerGroup,
    times: BasicScheduleTimeData[],
    dates: DateArg<Date>[],
): boolean {
    return times.some(({ time }) => {
        return dates.some((date) => identifier.includes(makeKey(target, group, date, time)));
    });
}
